import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useStore } from "react-redux";
import { useTranslation } from "react-i18next";
import { Selectors } from "../../../Everou/_reducers/app.reducer";
import { useGlobalNotification } from "../../../Common/_hooks/useGlobalNotification";
import { PMSIntegrationsActions } from "../../_actions/PMSIntegrationsActions";
import { PMSLocationsActions } from "../../_actions/PMSLocationsActions";
import { Integration, SyncStatusValue } from "../../../Everou/_modelsTS/Integration";

// Backend error codes for the force-sync endpoint.
const ERROR_INTEGRATION_NOT_FOUND = 682;
const ERROR_MANUAL_SYNC_NOT_ALLOWED = 699;

// Poll the location integrations every ~2.5s until the integration stops being
// 'running', giving up after ~30s (the result will be reflected next time the
// screen is opened).
const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 30000;

// Manual force-sync of a single integration within a location, with polling of
// its sync_status. loc_uid + uid identify the integration to sync.
export function useIntegrationSync(integration: Integration, locationUid: string) {

    const dispatch = useDispatch();
    const store = useStore();
    const { t } = useTranslation();
    const { notify } = useGlobalNotification();

    const [isSyncing, setIsSyncing] = useState(false);

    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const isMountedRef = useRef(true);

    const stopPolling = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        if (isMountedRef.current) setIsSyncing(false);
    }, []);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, []);

    const startPolling = useCallback(() => {
        const startedAt = Date.now();

        intervalRef.current = setInterval(async () => {
            try {
                // Refreshes the location integrations (badge, last sync and the
                // reservations pulled by this integration) from the backend.
                await dispatch(PMSLocationsActions.getIntegrations());
            } catch {
                stopPolling();
                return;
            }

            if (!isMountedRef.current) return;

            const list = Selectors.getPMSLocationIntegrations(store.getState());
            const updated = list?.find(({ uid }) => uid === integration.uid);
            const status = updated?.syncStatus;

            if (status === SyncStatusValue.RUNNING) {
                if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
                    stopPolling();
                    notify(t('mod_integrations_sync_taking_long'), 'info');
                }
                return;
            }

            stopPolling();

            if (status === SyncStatusValue.ERROR) {
                notify(updated?.syncError || t('mod_integrations_sync_failed'), 'error');
            } else if (status === SyncStatusValue.OK) {
                notify(t('mod_integrations_synced'), 'success');
            }
        }, POLL_INTERVAL_MS);
    }, [dispatch, store, integration.uid, notify, stopPolling, t]);

    const onSync = useCallback(async () => {
        if (isSyncing || !integration.uid) return;

        setIsSyncing(true);
        try {
            await dispatch(PMSIntegrationsActions.syncIntegration(locationUid, integration.uid));
            // 202 accepted -> integration is now 'running', start polling.
            startPolling();
        } catch (err: any) {
            setIsSyncing(false);
            const code = Number(err?.name);
            if (code === ERROR_MANUAL_SYNC_NOT_ALLOWED) {
                notify(t('mod_integrations_sync_not_allowed'), 'error');
            } else if (code === ERROR_INTEGRATION_NOT_FOUND) {
                notify(t('mod_integrations_sync_not_found'), 'error');
                dispatch(PMSLocationsActions.getIntegrations());
            } else {
                notify(err?.message || t('mod_integrations_sync_failed'), 'error');
            }
        }
    }, [dispatch, integration.uid, isSyncing, locationUid, notify, startPolling, t]);

    return { isSyncing, onSync };
}
